import type * as React from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { ActionButton, ActionButtonContainer } from '@/_containers';
import type { SvgIconProps } from '@/types';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = ({ ref, className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Item>) => (
  <AccordionPrimitive.Item ref={ref} className={twMerge(className)} {...props} />
);

const AccordionTrigger = ({
  ref,
  className,
  children,
  hideIcon,
  invisibleIcon,
  icon = ChevronDown,
  actions,
  actionsClassName,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  icon?: SvgIconProps;
  hideIcon?: boolean;
  invisibleIcon?: boolean;
  actions?: React.ReactNode;
  actionsClassName?: string;
}) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={twMerge(
        'flex flex-1 items-center justify-between font-medium outline-0 transition-all focus-within:outline-0 focus:outline-0 focus-visible:outline-0 active:outline-0 [&[data-state=open]_.evt-exp>svg]:rotate-180',
        className
      )}
      {...props}
    >
      {children}

      <article className={twMerge('flex items-center gap-1.5', actionsClassName)}>
        {actions}
        {invisibleIcon ? (
          <ActionButtonContainer className={`h-5 w-5 border-none pt-0 ${hideIcon ? 'hidden' : ''}`} />
        ) : (
          <ActionButtonContainer className={`h-5 w-5 border-none pt-0 ${hideIcon ? 'hidden' : ''}`}>
            <ActionButton icon={icon} title="Expand" size="xs" className="evt-exp" />
          </ActionButtonContainer>
        )}
      </article>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
);

const AccordionContent = ({
  ref,
  className,
  containerClassName,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content> & {
  containerClassName?: string;
}) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={twMerge(
      'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm transition-all',
      containerClassName
    )}
    {...props}
  >
    <div className={twMerge('pt-0 pb-0', className)}>{children}</div>
  </AccordionPrimitive.Content>
);

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
